import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { checkBusinessVerification, checkFirstCollabStatus, getUserProfile, publishCollabOffer } from '../../services/api';
import api from '../../services/api';
import FirstCollabActivation from './first-collab/FirstCollabActivation';
import { validateCollabStep } from './first-collab/collabFormShared';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// Rich Text Editor imports
import ReactQuill from '../common/RichTextEditor';
import { RadioGroup, Switch, FormField, Input } from '../common/design-system/Field';
import 'react-quill/dist/quill.snow.css';
// Date Picker imports
import { FothDateRangePicker } from '../common/DatePicker';
import {
  FaTrash,
  FaArrowLeft,
  FaPaperPlane,
  FaSave,
  FaCheck,
  FaUtensils, FaShoppingBag, FaPlaneDeparture, FaCalendarCheck, FaMusic, FaBed, FaDumbbell, FaSpa, FaGift, FaEllipsisH, FaMagic
} from 'react-icons/fa';
import './OfferStyles.scss';
import './CreateOffer.scss';
import { track } from '../../services/measurement';
import AICampaignAssistant from './AICampaignAssistant';
import { normalizeCountryId } from './AICampaignAssistant.utils';
import VerificationRequired from './VerificationRequired';
import FormErrorSummary from './FormErrorSummary';
import {
  scrollToAndFocusFirstError,
  scrollToAndFocusField,
  findStepForError,
  normalizeBackendErrors,
} from './collabValidationHelper';

// Constantes
const TYPE_CHOICES = [
  { id: '1', name: 'types.1', icon: <FaUtensils /> },
  { id: '2', name: 'types.2', icon: <FaShoppingBag /> },
  { id: '3', name: 'types.3', icon: <FaPlaneDeparture /> },
  { id: '4', name: 'types.4', icon: <FaCalendarCheck /> },
  { id: '5', name: 'types.5', icon: <FaMusic /> },
  { id: '6', name: 'types.6', icon: <FaBed /> },
  { id: '7', name: 'types.7', icon: <FaDumbbell /> },
  { id: '8', name: 'types.8', icon: <FaSpa /> },
  { id: '9', name: 'types.9', icon: <FaPlaneDeparture /> },
  { id: '10', name: 'types.10', icon: <FaGift /> },
  { id: '11', name: 'types.11', icon: <FaEllipsisH /> }
];

const FOLLOWERS_REQUIREMENTS = [
  { id: '1', name: 'requirements.1' },
  { id: '2', name: 'requirements.2' },
  { id: '3', name: 'requirements.3' },
  { id: '4', name: 'requirements.4' }
];

// Fallback estático
const STATIC_EXCHANGE_OPTIONS = [
  { id: 1, code: '1', name: 'Post' }, { id: 2, code: '2', name: 'Story' }, { id: 3, code: '3', name: 'Reel' }
];
const STATIC_TARGET_OPTIONS = [
  { id: 1, code: '1', name: 'Lifestyle' }, { id: 2, code: '2', name: 'Fashion' }, { id: 3, code: '3', name: 'Beauty' },
  { id: 4, code: '4', name: 'Food' }, { id: 5, code: '5', name: 'Wellness' }, { id: 6, code: '6', name: 'Entertainment' },
  { id: 7, code: '7', name: 'Travel' }, { id: 8, code: '8', name: 'Sports' }, { id: 9, code: '9', name: 'Tech' }
];

const DELIVERABLES_MODE_OPTIONS = [
  {
    id: 'open',
    titleKey: 'create.form.deliverables_mode_open',
    title: 'Open Deliverables',
    helpKey: 'create.form.deliverables_mode_open_help',
    help: 'Creators can publish the content they feel best represents the experience.'
  },
  {
    id: 'defined',
    titleKey: 'create.form.deliverables_mode_defined',
    title: 'Defined Requirements',
    helpKey: 'create.form.deliverables_mode_defined_help',
    help: 'Set exact quantities for Posts, Stories, and Reels.'
  }
];

const getOptionValue = (option) => String(option?.code ?? option?.id ?? option);

const buildDefinedDeliverablesCounts = (selectedOptions = [], currentCounts = {}) => (
  selectedOptions.reduce((counts, optionId) => {
    const numericCount = Number.parseInt(currentCounts[optionId], 10);
    counts[optionId] = Number.isNaN(numericCount) ? 1 : Math.max(numericCount, 0);
    return counts;
  }, {})
);

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];
const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png'];

const isImageFileValid = (file) => {
  if (!file) return false;
  const mime = file.type ? file.type.toLowerCase() : '';
  const name = file.name ? file.name.toLowerCase() : '';
  const isMimeValid = ALLOWED_IMAGE_TYPES.includes(mime);
  const isExtValid = ALLOWED_IMAGE_EXTENSIONS.some(ext => name.endsWith(ext));
  return isMimeValid || isExtValid;
};

const parseDateOnly = (dateValue) => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) return dateValue;
  const match = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const formatLocalDate = (dateValue) => {
  if (!dateValue) return '';
  if (typeof dateValue === 'string') {
    const match = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return match[0];
  }
  const d = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// ReactQuill configuration - Simplified for better stability
const quillModules = {
  toolbar: {
    container: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      [{ 'align': [] }],
      ['clean']
    ],
    handlers: {
      // Custom handlers can be added here if needed
    }
  },
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: false,
  }
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline',
  'list', 'bullet',
  'link', 'align'
];

// Animation Variants
const stepVariants = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: "easeInOut" } },
  exit: { opacity: 0, x: -30, transition: { duration: 0.3, ease: "easeInOut" } },
};

const CreateOffer = ({ mode = 'exchange' }) => {
  const { t } = useTranslation('collab');
  const { currentUser, isAuthenticated, refreshUser, subscriptionInfo } = useAuth();
  const maxCreators = Number(
    subscriptionInfo?.max_creators_per_collab ??
    subscriptionInfo?.active_subscription?.max_creators_per_collab ??
    0
  );
  const isUnlimitedCreators = Boolean(subscriptionInfo?.has_active_subscription && maxCreators === 0);
  const [isQuillLoaded, setIsQuillLoaded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [bypassFirstCollab, setBypassFirstCollab] = useState(
    () => new URLSearchParams(location.search).get('bypass') === '1'
  );
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  const [featuredImagePreview, setFeaturedImagePreview] = useState(null);
  const [additionalImagesPreview, setAdditionalImagesPreview] = useState([]);

  const getFutureDate = (daysToAdd) => {
    const date = new Date();
    date.setDate(date.getDate() + daysToAdd);
    return date;
  };

  const getMaximumCampaignDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date;
  };

  const [formData, setFormData] = useState({
    title: '',
    type: '',
    value: '',
    compensation_mode: mode,
    cash_currency: 'USD',
    quantity: 1,
    country: '',
    followers_requirements: '1',
    from_date: getFutureDate(1),
    to_date: getFutureDate(16),
    description: '',
    exchange_options: [],
    deliverables_counts: {},
    deliverables_mode: 'defined',
    target_options: [],
    terms_and_conditions: '',
    sponsor_this_offer: mode !== 'exchange',
    sponsor_amount: '',
    featured_image: null,
    additional_images: [],
    add_time_range: false,
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const isPaidCollab = mode === 'paid' || ['paid', 'hybrid'].includes(formData.compensation_mode) || Boolean(formData.sponsor_this_offer);
  const totalInvestment = isPaidCollab
    ? ((Number(formData.sponsor_amount) || 0) * (Number(formData.quantity) || 0))
    : 0;

  const [countries, setCountries] = useState([]);
  const [exchangeOptionsList, setExchangeOptionsList] = useState([]);
  const [targetOptionsList, setTargetOptionsList] = useState([]);

  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false);

  const applyAIAssistantData = (data) => {
    const nextFromDate = parseDateOnly(data.from_date);
    const nextToDate = parseDateOnly(data.to_date);
    const resolvedCountry = normalizeCountryId(data.country, countries) || data.country;
    let nextQuantity = data.quantity || formData.quantity;
    if (maxCreators > 0 && Number(nextQuantity) > maxCreators) {
      nextQuantity = maxCreators;
    }

    setFormData(prev => ({
      ...prev,
      title: data.title || prev.title,
      value: data.value ?? prev.value,
      quantity: maxCreators > 0 && Number(data.quantity) > maxCreators ? maxCreators : (data.quantity || prev.quantity),
      description: data.description ? `<p>${data.description}</p>` : prev.description,
      type: data.type || prev.type,
      followers_requirements: data.followers_requirements || prev.followers_requirements,
      exchange_options: Array.isArray(data.exchange_options) && data.exchange_options.length ? data.exchange_options.map(String) : prev.exchange_options,
      deliverables_mode: data.deliverables_mode || prev.deliverables_mode,
      deliverables_counts: data.deliverables_mode === 'open' ? {} : (data.deliverables_counts && Object.keys(data.deliverables_counts).length ? data.deliverables_counts : prev.deliverables_counts),
      country: resolvedCountry || prev.country,
      from_date: nextFromDate && !Number.isNaN(nextFromDate.getTime()) ? nextFromDate : prev.from_date,
      to_date: nextToDate && !Number.isNaN(nextToDate.getTime()) ? nextToDate : prev.to_date,
      add_time_range: data.add_time_range !== undefined ? data.add_time_range : prev.add_time_range,
      target_options: Array.isArray(data.target_options) && data.target_options.length ? data.target_options.map(String) : prev.target_options,
      terms_and_conditions: data.terms_and_conditions ? `<p>${data.terms_and_conditions.replace(/\n/g, '<br/>')}</p>` : prev.terms_and_conditions,
      sponsor_this_offer: data.sponsor_this_offer !== undefined ? data.sponsor_this_offer : prev.sponsor_this_offer,
      sponsor_amount: data.sponsor_amount || prev.sponsor_amount,
    }));
    setErrors({});
    toast.success(t('create.ai_success'));
  };

  useEffect(() => {
    if (location.state?.duplicateOfferData) {
      const dataToLoad = location.state.duplicateOfferData;
      const collabDataToLoad = Object.fromEntries(
        Object.entries(dataToLoad).filter(([key]) => key !== 'add_booking'),
      );
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const fallbackStart = new Date(today);
      fallbackStart.setDate(fallbackStart.getDate() + 1);
      const duplicatedStart = parseDateOnly(dataToLoad.from_date);
      const duplicatedEnd = parseDateOnly(dataToLoad.to_date);
      const nextStart = duplicatedStart && duplicatedStart >= today ? duplicatedStart : fallbackStart;
      const latestEnd = new Date(nextStart);
      latestEnd.setDate(latestEnd.getDate() + 30);
      const nextEnd = duplicatedEnd && duplicatedEnd > nextStart && duplicatedEnd <= latestEnd
        ? duplicatedEnd
        : new Date(new Date(nextStart).setDate(nextStart.getDate() + 15));
      toast.info("Offer data loaded for duplication.");

      setFormData(prev => ({
        ...prev,
        ...collabDataToLoad,
        from_date: nextStart,
        to_date: nextEnd,
        featured_image: null,
        additional_images: [],
      }));

      if (dataToLoad.initialFeaturedImageUrl) setFeaturedImagePreview(dataToLoad.initialFeaturedImageUrl);
      if (dataToLoad.initialAdditionalImageUrls) setAdditionalImagesPreview(dataToLoad.initialAdditionalImageUrls);

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!isAuthenticated || currentUser?.user_type !== 'business') {
        setLoading(false);
        return;
      }
      try {
        const [verification, countriesRes, profile, exchangeOpt, targetOpt] = await Promise.all([
          checkBusinessVerification(),
          api.get('/countries/'),
          getUserProfile(),
          api.get('/collabs/exchange-options/'),
          api.get('/collabs/target-options/'),
        ]);

        let verificationData = verification.data;
        if (verificationData && verificationData.is_first_collab === undefined) {
          try {
            const firstCollabRes = await checkFirstCollabStatus();
            verificationData = {
              ...verificationData,
              is_first_collab: Boolean(firstCollabRes.data?.is_first_collab),
            };
          } catch {
            // Keep default
          }
        }

        setVerificationStatus(verificationData);
        setCountries(countriesRes.data);
        if (profile?.country) {
          setFormData(prev => ({ ...prev, country: profile.country }));
        }
        setExchangeOptionsList(exchangeOpt.data.length ? exchangeOpt.data : STATIC_EXCHANGE_OPTIONS);
        setTargetOptionsList(targetOpt.data.length ? targetOpt.data : STATIC_TARGET_OPTIONS);

      } catch (err) {
        toast.error("Failed to load initial data. Using fallbacks.");
        // Use fallbacks on error
        setExchangeOptionsList(STATIC_EXCHANGE_OPTIONS);
        setTargetOptionsList(STATIC_TARGET_OPTIONS);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [currentUser, isAuthenticated]);


  const handleImageChange = (e, type) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const validFiles = files.filter(isImageFileValid);
    if (validFiles.length !== files.length) {
      toast.error(t('create.errors.invalid_image_format', 'Solo se permiten imágenes en formato JPG o PNG.'));
    }
    if (!validFiles.length) {
      e.target.value = '';
      return;
    }

    if (type === 'featured') {
      const file = validFiles[0];
      setFormData(prev => ({ ...prev, featured_image: file }));
      setFeaturedImagePreview(URL.createObjectURL(file));
      if (errors.featured_image) {
        setErrors(prev => ({ ...prev, featured_image: null }));
      }
    } else {
      const newImages = [...formData.additional_images, ...validFiles].slice(0, 5);
      setFormData(prev => ({ ...prev, additional_images: newImages }));
      const newPreviews = newImages.map(file => (typeof file === 'string' ? file : URL.createObjectURL(file)));
      setAdditionalImagesPreview(newPreviews);
    }
    e.target.value = '';
  };

  const handleRemoveImage = (index, type) => {
    if (type === 'featured') {
      setFormData(prev => ({ ...prev, featured_image: null }));
      setFeaturedImagePreview(null);
    } else {
      const newImages = formData.additional_images.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, additional_images: newImages }));
      const newPreviews = newImages.map(file => (typeof file === 'string' ? file : URL.createObjectURL(file)));
      setAdditionalImagesPreview(newPreviews);
    }
  };

  const validateStep = (stepToValidate = currentStep, shouldScroll = true) => {
    const { errors: newErrors, isValid } = validateCollabStep({
      step: stepToValidate,
      formData,
      mode,
      maxCreators,
      t,
      hasFeaturedPreview: Boolean(featuredImagePreview || formData.featured_image),
    });
    setErrors(newErrors);
    if (!isValid && shouldScroll) {
      scrollToAndFocusFirstError(newErrors, stepToValidate, 'offer');
    }
    return isValid;
  };

  const validateAllSteps = () => {
    for (let s = 1; s <= 3; s++) {
      const stepValid = validateStep(s, false);
      if (!stepValid) {
        if (currentStep !== s) {
          setCurrentStep(s);
        }
        setTimeout(() => {
          validateStep(s, true);
        }, 120);
        return false;
      }
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep(currentStep, true)) {
      setErrors({});
      setCurrentStep(prev => prev + 1);
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        try {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch {
          window.scrollTo(0, 0);
        }
      }
    }
  };

  const handlePreviousStep = () => {
    setErrors({});
    setCurrentStep(prev => prev - 1);
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        window.scrollTo(0, 0);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
    if (name === 'sponsor_amount' && errors.sponsor_amount) {
      setErrors(prev => ({ ...prev, sponsor_amount: null }));
    }
    if (name === 'value' && errors.value) {
      setErrors(prev => ({ ...prev, value: null }));
    }
    if (name === 'quantity' && errors.quantity) {
      setErrors(prev => ({ ...prev, quantity: null }));
    }
    if (name === 'country' && errors.country) {
      setErrors(prev => ({ ...prev, country: null }));
    }
  };

  // Rich text editor handlers
  const handleRichTextChange = (content, fieldName) => {
    // Clean empty content (Quill sends '<p><br></p>' for empty content)
    const cleanContent = content === '<p><br></p>' ? '' : content;
    setFormData(prev => ({ ...prev, [fieldName]: cleanContent }));

    // Clear any existing error for this field
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: null }));
    }
  };

  // Safe Quill loading - Delay to ensure DOM is ready
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsQuillLoaded(true);
    }, 100); // Small delay to ensure DOM is ready

    return () => clearTimeout(timer);
  }, []);

  // Date range handler
  const handleDateRangeChange = ({ startDate, endDate }) => {
    setFormData(prev => ({
      ...prev,
      from_date: startDate,
      to_date: endDate
    }));
    if (errors.dates || errors.from_date || errors.to_date) {
      setErrors(prev => ({ ...prev, dates: null, from_date: null, to_date: null }));
    }
  };

  const handleMultiSelect = (name, optionId) => {
    const currentSelection = formData[name];
    const isSelected = currentSelection.includes(optionId);
    const newSelection = isSelected
      ? currentSelection.filter(id => id !== optionId)
      : [...currentSelection, optionId];
    
    // Si se deselecciona de exchange_options, también remover su count
    const newCounts = { ...formData.deliverables_counts };
    if (name === 'exchange_options') {
      if (isSelected) {
        delete newCounts[optionId];
      } else if (formData.deliverables_mode === 'defined') {
        newCounts[optionId] = 1;
      }
    }

    setFormData(prev => ({ 
      ...prev, 
      [name]: newSelection,
      deliverables_counts: name === 'exchange_options' ? newCounts : prev.deliverables_counts
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleDeliverableCountChange = (optionId, count) => {
    const val = Number.parseInt(count, 10);
    setFormData(prev => ({
      ...prev,
      deliverables_counts: {
        ...prev.deliverables_counts,
        [optionId]: Number.isNaN(val) ? 0 : Math.max(val, 0)
      }
    }));
    if (errors.deliverables_counts) {
      setErrors(prev => ({ ...prev, deliverables_counts: null }));
    }
  };

  const handleDeliverablesModeChange = (mode) => {
    setFormData(prev => ({
      ...prev,
      deliverables_mode: mode,
      deliverables_counts: mode === 'defined'
        ? buildDefinedDeliverablesCounts(prev.exchange_options, prev.deliverables_counts)
        : {}
    }));
    setErrors(prev => ({ ...prev, exchange_options: null, deliverables_counts: null }));
  };

  const submitOfferData = async (status = 'draft') => {
    if (status === 'active' && !validateAllSteps()) {
      toast.error(t('create.errors.publish_fill'));
      return;
    }

    const loader = status === 'active' ? setIsPublishing : setSubmitting;
    loader(true);

    const normalizedFormData = {
      ...formData,
      add_booking: false,
      compensation_mode: mode,
      sponsor_this_offer: mode !== 'exchange' || Boolean(formData.sponsor_this_offer),
      value: formData.value,
      deliverables_counts: formData.deliverables_mode === 'defined'
        ? buildDefinedDeliverablesCounts(formData.exchange_options, formData.deliverables_counts)
        : {}
    };

    const data = new FormData();
    Object.keys(normalizedFormData).forEach(key => {
      if (key === 'additional_images') {
        normalizedFormData.additional_images.forEach(file => data.append('additional_images', file));
      } else if (Array.isArray(normalizedFormData[key])) {
        normalizedFormData[key].forEach(item => data.append(key, item));
      } else if (key === 'deliverables_counts') {
        data.append(key, JSON.stringify(normalizedFormData[key]));
      } else if (normalizedFormData[key] !== null) {
        // Convert dates safely using local calendar parts to prevent UTC offset shifting
        if (key === 'from_date' || key === 'to_date') {
          data.append(key, formatLocalDate(normalizedFormData[key]));
        } else if (normalizedFormData[key] instanceof Date) {
          data.append(key, formatLocalDate(normalizedFormData[key]));
        } else {
          data.append(key, normalizedFormData[key]);
        }
      }
    });
    try {
      const createResponse = await api.post('/collabs/collab/', data);

      const createdOffer = createResponse.data;
      track('offer_created', { flow: mode, status });
      if (status === 'active') {
        if (mode === 'paid' || normalizedFormData.sponsor_this_offer) {
          toast.info(t('payment_release.created_paid_help'));
          navigate(`/business/collabs/${createdOffer.id}/journey`);
          return;
        }
        try {
          await publishCollabOffer(createdOffer.id);
          track('offer_published', { flow: mode });
          toast.success(t('list.messages.activate_success'));
          navigate(`/business/collabs/${createdOffer.id}/journey`);
        } catch (publishError) {
          if (publishError.response?.status === 402) {
            toast.info(publishError.response?.data?.error || t('payment.required', 'A subscription is required to publish this collab.'));
            navigate(`/business/payment/required?offerId=${createdOffer.id}`);
          } else {
            throw publishError;
          }
        }
      } else {
        toast.success(t('list.messages.duplicate_success'));
        navigate(`/business/collabs/${createdOffer.id}/journey`);
      }
      refreshUser();
    } catch (error) {
       const backendFieldErrors = normalizeBackendErrors(error.response?.data);
       if (Object.keys(backendFieldErrors).length > 0) {
         setErrors(backendFieldErrors);
         const targetStep = findStepForError(backendFieldErrors);
         if (currentStep !== targetStep) {
           setCurrentStep(targetStep);
         }
         setTimeout(() => {
           scrollToAndFocusFirstError(backendFieldErrors, targetStep, 'offer');
         }, 150);
       }
       toast.error(error.response?.data?.error || error.response?.data?.detail || (status === 'active' ? t('list.messages.activate_error') : t('list.messages.duplicate_error')));
    } finally {
      loader(false);
    }
  };

  const renderProgressBar = () => (
    <div className="v2-progress">
      {[t('create.steps.details'), t('create.steps.requirements'), t('create.steps.media')].map((label, index) => {
        const stepNum = index + 1;
        const isActive = currentStep === stepNum;
        const isCompleted = currentStep > stepNum;
        
        return (
          <div key={label} className={`v2-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
             <div className="v2-step-bar" />
             <div className="v2-step-text">{stepNum}. {label}</div>
          </div>
        )
      })}
    </div>
  );

  const renderAIPromptButton = () => (
    <button
      type="button"
      className="v2-ai-btn"
      onClick={() => setIsAIAssistantOpen(true)}
    >
      <FaMagic /> {t('create.ai_btn')}
    </button>
  );

  const renderFormStep = () => {
    switch (currentStep) {
      case 1:
        return (
           <motion.div key="step1" variants={stepVariants} initial="hidden" animate="visible" exit="exit" className="v2-form-step">
             <div className="v2-form-grid">
               <div className="v2-form-group col-span-2">
                 <label htmlFor="offer-title">{t('create.form.title_label')}</label>
                 <input id="offer-title" name="title" value={formData.title} onChange={handleChange} className={`v2-input ${errors.title ? 'error' : ''}`} placeholder={t('create.form.title_placeholder')} />
                 {errors.title && <p className="v2-error">{errors.title}</p>}
               </div>
 
               <div className="v2-form-group">
                 <label htmlFor={mode === 'paid' ? 'offer-sponsor-amount' : 'offer-value'}>{mode === 'paid'
                   ? t('create.form.paid_amount_label', 'Payment per creator')
                   : t('create.form.budget_label')}</label>
                 <div className="v2-money-input">
                   <span>{formData.cash_currency}</span>
                   <input
                     id={mode === 'paid' ? 'offer-sponsor-amount' : 'offer-value'}
                     name={mode === 'paid' ? 'sponsor_amount' : 'value'}
                     type="number"
                     min="0"
                     step="0.01"
                     value={mode === 'paid' ? formData.sponsor_amount : formData.value}
                     onChange={handleChange}
                     className={`v2-input ${errors.value || errors.sponsor_amount ? 'error' : ''}`}
                     placeholder={mode === 'paid'
                       ? t('create.form.paid_amount_placeholder', 'e.g. 150.00')
                       : t('create.form.budget_placeholder')}
                   />
                 </div>
                {mode === 'paid' && <span className="v2-field-help">{t('create.form.paid_amount_help', 'The creator receives this amount after the campaign is approved.')}</span>}
                {errors.value && <p className="v2-error">{errors.value}</p>}
                {errors.sponsor_amount && <p className="v2-error">{errors.sponsor_amount}</p>}
              </div>

              {mode === 'paid' && (
                <div className="v2-form-group col-span-2">
                  <label htmlFor="offer-in-kind-value">{t('create.form.in_kind_value_label')}</label>
                  <div className="v2-money-input">
                    <span>{formData.cash_currency}</span>
                    <input
                      id="offer-in-kind-value"
                      name="value"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.value}
                      onChange={handleChange}
                      className={`v2-input ${errors.value ? 'error' : ''}`}
                      placeholder={t('create.form.in_kind_value_placeholder')}
                    />
                  </div>
                  <span className="v2-field-help">{t('create.form.in_kind_value_help')}</span>
                  {errors.value && <p className="v2-error">{errors.value}</p>}
                </div>
              )}
 
               <div className="v2-form-group">
                 <label htmlFor="offer-quantity">{t('create.form.creators_label')}</label>
                 <input
                   id="offer-quantity"
                   name="quantity"
                   type="number"
                   min="1"
                   max={maxCreators > 0 ? maxCreators : undefined}
                   value={formData.quantity}
                   onChange={handleChange}
                   className={`v2-input ${errors.quantity ? 'error' : ''}`}
                   placeholder={t('create.form.creators_placeholder')}
                 />
                 {maxCreators > 0 && (
                   <span className="v2-field-help">
                     {t('create.form.creators_limit_hint', { count: maxCreators, defaultValue: `Tu plan permite hasta ${maxCreators} creadores por collab.` })}
                   </span>
                 )}
                 {isUnlimitedCreators && (
                   <span className="v2-field-help">
                     {t('create.form.creators_unlimited_hint', 'Tu plan permite creadores ilimitados por collab.')}
                   </span>
                 )}
                 {errors.quantity && <p className="v2-error">{errors.quantity}</p>}
               </div>

                {isPaidCollab && Boolean(formData.sponsor_amount) && Number(formData.quantity) > 0 && (
                  <div className="v2-form-group col-span-2">
                    <div className="v2-paid-investment-card">
                      <div className="v2-paid-investment-header">
                        <span className="v2-paid-investment-label">{t('create.form.total_investment_title', 'Inversión Total Estimada')}</span>
                        <span className="v2-paid-investment-amount">
                          {formData.cash_currency} {totalInvestment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <p className="v2-paid-investment-detail">
                        {t('create.form.total_investment_calculation', {
                          count: formData.quantity,
                          amount: `${formData.cash_currency} ${Number(formData.sponsor_amount || 0).toFixed(2)}`,
                          defaultValue: `${formData.quantity} creadores × ${formData.cash_currency} ${Number(formData.sponsor_amount || 0).toFixed(2)} por creador. La tarifa de plataforma e impuestos se calcularán en el pago.`
                        })}
                      </p>
                    </div>
                  </div>
                )}
 
               <div className="v2-form-group col-span-2">
                 <label>{t('create.form.dates_label')}</label>
                 <FothDateRangePicker
                   startDate={formData.from_date}
                   endDate={formData.to_date}
                   onChange={handleDateRangeChange}
                   startLabel={t('create.form.dates_start')}
                   endLabel={t('create.form.dates_end')}
                   startPlaceholder={t('create.form.dates_start_placeholder')}
                   endPlaceholder={t('create.form.dates_end_placeholder')}
                   startError={errors.from_date}
                   endError={errors.to_date}
                   maxDays={30}
                   required={true}
                   minDate={new Date()}
                   maxDate={getMaximumCampaignDate()}
                   startId="offer-start-date"
                   endId="offer-end-date"
                 />
                 {errors.dates && <p className="v2-error">{errors.dates}</p>}
               </div>
 
               <div className="v2-form-group col-span-2">
                 <label htmlFor="offer-country">{t('create.form.country_label')}</label>
                 <select id="offer-country" name="country" value={formData.country} onChange={handleChange} className={`v2-select ${errors.country ? 'error' : ''}`}>
                   <option value="">{t('create.form.country_placeholder')}</option>
                   {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                 </select>
                 {errors.country && <p className="v2-error">{errors.country}</p>}
               </div>
 
               <div className="v2-form-group col-span-2">
                 <div className="v2-editor-label" id="offer-description-editor-label">{t('create.form.description_label')}</div>
                 <div className={`v2-rich-text ${errors.description ? 'error' : ''}`}>
                   {isQuillLoaded ? (
                     <ReactQuill
                       id="offer-description-editor"
                       labelledBy="offer-description-editor-label" error={errors.description}
                       value={formData.description || ''}
                       onChange={(content) => handleRichTextChange(content, 'description')}
                       modules={quillModules}
                       formats={quillFormats}
                       placeholder={t('create.form.description_placeholder')}
                       theme="snow"
                     />
                   ) : (
                     <div style={{padding: '16px', color: '#a6a6a6'}}>{t('create.form.loading_editor')}</div>
                   )}
                 </div>
                 {errors.description && <p id="offer-description-editor-error" className="v2-error">{errors.description}</p>}
               </div>
 
                <div className="v2-form-group col-span-2">
                  <Switch label={t('create.form.time_range_label')} hint={t('create.form.time_range_help')}
                    checked={formData.add_time_range} onChange={event => {
                      const checked = event.target.checked;
                      setFormData(previous => ({ ...previous, add_time_range: checked }));
                    }} />
                </div>
 
               <div className="v2-form-group col-span-2">
                 <RadioGroup label={t('create.form.type_label')} name="collab-type" value={formData.type}
                   onChange={type => {
                     setFormData(p => ({ ...p, type }));
                     if (errors.type) setErrors(prev => ({ ...prev, type: null }));
                   }}
                   options={TYPE_CHOICES.map(type => ({ value: type.id, label: <><span aria-hidden="true">{type.icon}</span> {t(`types.${type.id}`)}</> }))} />
                 {errors.type && <p className="v2-error">{errors.type}</p>}
               </div>
            </div>
          </motion.div>
        );
      case 2:
        return (
           <motion.div key="step2" variants={stepVariants} initial="hidden" animate="visible" exit="exit" className="v2-form-step">
             <div className="v2-form-grid">
               <div className="v2-form-group col-span-2">
                 <RadioGroup label={t('create.form.followers_label')} name="collab-followers" value={formData.followers_requirements}
                   onChange={followers_requirements => setFormData(p => ({ ...p, followers_requirements }))}
                   options={FOLLOWERS_REQUIREMENTS.map(req => ({ value: req.id, label: t(`requirements.${req.id}`) }))} />
               </div>
               
               <div className="v2-form-group col-span-2">
                 <RadioGroup label={t('create.form.deliverables_mode_label', 'Deliverable Requirements')} name="collab-deliverables-mode"
                   value={formData.deliverables_mode} onChange={handleDeliverablesModeChange}
                   options={DELIVERABLES_MODE_OPTIONS.map(mode => ({ value: mode.id, label: t(mode.titleKey, mode.title), hint: t(mode.helpKey, mode.help) }))} />
               </div>

               <div className="v2-form-group col-span-2">
                 <fieldset className="v2-form-group-reset"><legend>{formData.deliverables_mode === 'open' ? t('create.form.exchange_open_label', 'Preferred Content Formats') : t('create.form.exchange_label')}</legend>
                 {formData.deliverables_mode === 'open' && (
                   <motion.div
                     initial={{ opacity: 0, y: -6 }}
                     animate={{ opacity: 1, y: 0 }}
                     exit={{ opacity: 0, y: -6 }}
                     className="v2-open-deliverables-note"
                   >
                     {t(
                       'create.form.deliverables_open_note',
                       'Creators are free to submit any content they generate. Select formats only if you want to signal preferences.'
                     )}
                   </motion.div>
                 )}
                 <div className="v2-deliverables-list">
                    {exchangeOptionsList.map(opt => {
                      const optValue = getOptionValue(opt);
                      const isSelected = formData.exchange_options.includes(optValue);
                      return (
                        <div key={optValue || opt.id || opt.code} className={`v2-deliverable-item ${isSelected ? 'selected' : ''}`}>
                          <button
                            type="button"
                            className="v2-deliverable-main"
                            onClick={() => handleMultiSelect('exchange_options', optValue)}
                            aria-pressed={isSelected}
                          >
                            <div className="v2-checkbox-custom">
                              {isSelected && <FaCheck />}
                            </div>
                            <span className="v2-deliverable-name">{t(`create.form.exchange_options.${optValue}`, opt.name)}</span>
                          </button>
                          
                          <AnimatePresence>
                            {isSelected && formData.deliverables_mode === 'defined' && (
                              <motion.div 
                                initial={{ opacity: 0, width: 0 }} 
                                animate={{ opacity: 1, width: 'auto' }} 
                                exit={{ opacity: 0, width: 0 }}
                                className="v2-deliverable-counter"
                              >
                                <label htmlFor={`create-deliverable-${optValue}`}>{t('create.form.quantity_label', 'Cantidad:')}</label>
                                <input 
                                  id={`create-deliverable-${optValue}`}
                                  type="number" 
                                  min="0"
                                  value={formData.deliverables_counts[optValue] ?? 1}
                                  onChange={(e) => handleDeliverableCountChange(optValue, e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                 {errors.exchange_options && <p className="v2-error">{errors.exchange_options}</p>}
                 {errors.deliverables_counts && <p className="v2-error">{errors.deliverables_counts}</p>}
               </fieldset></div>
 
               <div className="v2-form-group col-span-2">
                 <fieldset className="v2-form-group-reset"><legend>{t('create.form.target_label')}</legend>
                 <div className="v2-pill-group">
                   {targetOptionsList.map(opt => {
                     const optValue = getOptionValue(opt);
                     return (
                       <button
                         type="button"
                         key={optValue || opt.id || opt.code}
                         className={`v2-pill ${formData.target_options.includes(optValue) ? 'selected' : ''}`}
                         onClick={() => handleMultiSelect('target_options', optValue)}
                       >
                         {t(`create.form.target_options.${optValue}`, opt.name)}
                       </button>
                     );
                   })}
                 </div>
                 {errors.target_options && <p className="v2-error">{errors.target_options}</p>}
               </fieldset></div>
             </div>
           </motion.div>
        );
      case 3:
        return (
           <motion.div key="step3" variants={stepVariants} initial="hidden" animate="visible" exit="exit" className="v2-form-step">
             <div className="v2-form-grid">
               <div className="v2-form-group col-span-2">

                 {featuredImagePreview ? (
                   <div className="v2-image-preview" style={{maxWidth: '200px'}}>
                     <img src={featuredImagePreview} alt="Featured" />
                     <button type="button" aria-label={t('common:ui.removeImage')} onClick={() => handleRemoveImage(null, 'featured')} className="v2-remove-btn">
                       <FaTrash size={12} />
                     </button>
                   </div>
                 ) : null}
                  <FormField id="featuredImageInput" label={t('create.form.featured_image_label')} hint={t('create.form.featured_image_help', 'Formatos permitidos: JPG, PNG')} error={errors.featured_image}>
                    <Input type="file" onChange={e => handleImageChange(e, 'featured')} accept="image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png" />
                  </FormField>
                </div>
 
                <div className="v2-form-group col-span-2">

                  <div className="v2-additional-images">
                    {additionalImagesPreview.map((src, i) => (
                      <div key={i} className="v2-image-preview">
                        <img src={src} alt={`Additional ${i + 1}`} />
                        <button type="button" aria-label={`${t('common:ui.removeImage')} ${i + 1}`} onClick={() => handleRemoveImage(i, 'additional')} className="v2-remove-btn">
                          <FaTrash size={12} />
                        </button>
                      </div>
                    ))}

                  </div>
                  <FormField id="additionalImagesInput" label={t('create.form.additional_images_label')} hint={t('create.form.additional_images_hint', 'Máx. 5 imágenes (JPG, PNG)')}>
                    <Input type="file" multiple disabled={additionalImagesPreview.length >= 5}
                      onChange={e => handleImageChange(e, 'additional')} accept="image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png" />
                  </FormField>
               </div>
 
               <div className="v2-form-group col-span-2">
                 <div className="v2-editor-label" id="offer-terms-editor-label">{t('create.form.terms_label')}</div>
                 <div className={`v2-rich-text ${errors.terms_and_conditions ? 'error' : ''}`} style={{minHeight: '180px'}}>
                   <ReactQuill
                     id="offer-terms-editor"
                       labelledBy="offer-terms-editor-label" error={errors.terms_and_conditions}
                     value={formData.terms_and_conditions}
                     onChange={(content) => handleRichTextChange(content, 'terms_and_conditions')}
                     modules={quillModules}
                     formats={quillFormats}
                     placeholder={t('create.form.terms_placeholder')}
                     theme="snow"
                   />
                 </div>
                 {errors.terms_and_conditions && <p id="offer-terms-editor-error" className="v2-error">{errors.terms_and_conditions}</p>}
               </div>
             </div>
           </motion.div>
        );
      default: return null;
    }
  };

  if (currentUser?.user_type === 'creator') {
    return <Navigate to="/creator/feed" replace />;
  }

  if (loading) {
    return <div className="v2-create-offer-page" style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}><div className="loading-spinner" /></div>;
  }

   if (!isAuthenticated || currentUser?.user_type !== 'business') {
     return <div className="v2-create-offer-page" style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}><p>{t('create.actions.login_business')}</p></div>;
   }

  if (!verificationStatus?.is_verified) {
    return <VerificationRequired />;
  }

  if (!bypassFirstCollab && verificationStatus?.is_first_collab && !location.state?.duplicateOfferData) {
    return <FirstCollabActivation initialMode={mode} onExit={() => setBypassFirstCollab(true)} />;
  }

  return (
    <div className="v2-create-offer-page">
      <AICampaignAssistant
        isOpen={isAIAssistantOpen}
        onClose={() => setIsAIAssistantOpen(false)}
        onApplyData={applyAIAssistantData}
        currentFormData={formData}
        mode="create"
        compensationMode={mode}
        countries={countries}
      />
      
      <div className="v2-container">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="v2-card">
          
           <div className="v2-header">
             <div>
               <div className="v2-title-row">
                 <div className={`v2-collab-badge ${mode === 'paid' ? 'paid' : ''}`}>
                   {mode === 'paid' ? 'PAID COLLAB' : 'EXCHANGE COLLAB'}
                 </div>
                 <h2 className="v2-title">{mode === 'paid'
                   ? t('create.paid_title', 'Create a paid collaboration')
                   : t('create.title')}</h2>
               </div>
               <p className="v2-subtitle">{mode === 'paid'
                 ? t('create.paid_subtitle', 'Set a clear creator fee, define the campaign, and manage approval-based payouts securely.')
                 : t('create.subtitle')}</p>
                {mode === 'paid' && (
                  <div className="v2-paid-intro">
                    <FaCheck /> <span>{t('create.paid_intro', 'Creator funds are handled through FOTH and released after delivery review.')}</span>
                  </div>
                )}
                {mode === 'paid' && (
                  <div className={`v2-subscription-status ${subscriptionInfo?.has_active_subscription ? 'active' : ''}`} role="status">
                    <span className="v2-subscription-status-dot" aria-hidden="true" />
                    <span>
                      <strong>{subscriptionInfo?.has_active_subscription
                        ? t('create.subscription_active_title', 'Subscription active')
                        : t('create.subscription_required_title', 'Subscription required to fund')}</strong>
                      <small>{subscriptionInfo?.has_active_subscription
                        ? t('create.subscription_active_help', 'You can continue to the creator payment step after saving this collab.')
                        : t('create.subscription_required_help', 'You can create this draft for free. Activate any plan before paying your creators.')}</small>
                    </span>
                  </div>
                )}
             </div>
             {renderAIPromptButton()}
           </div>
          
          {renderProgressBar()}

          <form onSubmit={(e) => e.preventDefault()}>
            <AnimatePresence mode="wait">
              {renderFormStep()}
            </AnimatePresence>

            {currentStep === totalSteps && (
              <div className="v2-fairness-warning">
                {t('create.fairness_warning')}
              </div>
            )}

            <FormErrorSummary
              errors={errors}
              currentStep={currentStep}
              prefix="offer"
              onFieldClick={(fieldKey) => scrollToAndFocusField(fieldKey, 'offer')}
              t={t}
            />

            <div className="v2-form-actions">
              <button
                type="button"
                 className="v2-btn secondary"
                 onClick={handlePreviousStep}
                 disabled={currentStep === 1 || submitting || isPublishing}
               >
                 <FaArrowLeft /> {t('create.actions.prev')}
               </button>

               {currentStep < totalSteps ? (
                 <button type="button" className="v2-btn primary" onClick={handleNextStep}>
                   {t('create.actions.next')}
                 </button>
               ) : (
                <div className="v2-actions-right">
                  <button
                    type="button"
                     onClick={() => submitOfferData('draft')}
                     className="v2-btn secondary"
                     disabled={submitting || isPublishing}
                   >
                     {submitting ? t('create.actions.saving') : <><FaSave /> {t('create.actions.save_draft')}</>}
                   </button>
                   <button
                     type="button"
                      onClick={() => submitOfferData('active')}
                     className="v2-btn primary"
                     disabled={submitting || isPublishing}
                    >
                      {isPublishing ? t('create.actions.publishing') : <><FaPaperPlane /> {mode === 'paid'
                        ? (subscriptionInfo?.has_active_subscription
                          ? t('create.actions.continue_funding', 'Continue to funding')
                          : t('create.actions.save_review', 'Save draft and review'))
                        : t('create.actions.publish')}</>}
                    </button>
                </div>
              )}
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateOffer;
